import { useCallback, useEffect, useState } from 'react';

import { useMerchant } from '../contexts/MerchantContext';
import { getChildOverrideSaveBarDetailText } from '../util/parentSettingSave';

/**
 * Child-merchant UX: lock controls while a setting is inherited until the user chooses Customize.
 */
export function useChildInheritedConfigEdit(settingInherited: boolean, readOnly = false) {
  const { partnerMerchantId, hasDescendants } = useMerchant();
  const isChildMerchant = Boolean(partnerMerchantId);
  const [isCustomizingInherited, setIsCustomizingInherited] = useState(false);

  const showInheritedCallouts = isChildMerchant && settingInherited;

  useEffect(() => {
    setIsCustomizingInherited(false);
  }, [settingInherited]);

  const controlsDisabled = readOnly || (showInheritedCallouts && !isCustomizingInherited);

  const onCustomize = useCallback(() => setIsCustomizingInherited(true), []);
  const resetCustomizing = useCallback(() => setIsCustomizingInherited(false), []);

  const isInheritedCustomizeSession = showInheritedCallouts && isCustomizingInherited;

  const isChildInheritedSaveBarOpen = useCallback(
    (isFormDirty: boolean) => isFormDirty || isInheritedCustomizeSession,
    [isInheritedCustomizeSession],
  );

  /** Save is allowed while customizing to persist a local override, even if values match the parent. */
  const isChildInheritedSaveEnabled = useCallback(
    (isFormDirty: boolean) => isFormDirty || isInheritedCustomizeSession,
    [isInheritedCustomizeSession],
  );

  const getSaveBarInheritanceProps = useCallback(
    (
      isSaveBarOpen: boolean,
      dirtyInheritableLabels: string[],
      pageInheritableLabels: string[] = [],
    ) => {
      if (!isSaveBarOpen) {
        return { inheritableSettingLabels: [] as string[], detailTextOverride: undefined };
      }
      const suppressParentHierarchyCopy = isChildMerchant && isCustomizingInherited;
      const labelsForOverrideDetail =
        dirtyInheritableLabels.length > 0 ? dirtyInheritableLabels : pageInheritableLabels;
      return {
        inheritableSettingLabels: suppressParentHierarchyCopy ? [] : dirtyInheritableLabels,
        detailTextOverride:
          isChildMerchant && isCustomizingInherited
            ? getChildOverrideSaveBarDetailText(labelsForOverrideDetail, { hasDescendants })
            : undefined,
      };
    },
    [hasDescendants, isChildMerchant, isCustomizingInherited],
  );

  return {
    isChildMerchant,
    showInheritedCallouts,
    isCustomizingInherited,
    isInheritedCustomizeSession,
    isChildInheritedSaveBarOpen,
    isChildInheritedSaveEnabled,
    controlsDisabled,
    onCustomize,
    resetCustomizing,
    getSaveBarInheritanceProps,
  };
}
